import type { ReactNode } from "react";
import { LegalPage } from "../components/LegalPage";
import {
  getAddressLines,
  getDisputeResolutionText,
  getProviderName,
  hasCommercialRegister,
  hasIncompleteLegalProfile,
  hasRepresentative,
  hasVatId,
  legalProfile,
  missingLegalChecklist,
} from "../legal";

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-stone-200 py-4 last:border-b-0 md:grid-cols-[220px,1fr] md:gap-6">
      <dt className="text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</dt>
      <dd className="text-base leading-relaxed text-gray-800">{children}</dd>
    </div>
  );
}

export function Impressum() {
  return (
    <LegalPage
      title="Impressum"
      intro={
        <p>
          Anbieterkennzeichnung nach § 5 Digitale-Dienste-Gesetz (DDG) sowie allgemeine
          Informationsangaben für diesen Internetauftritt.
        </p>
      }
      notice={
        hasIncompleteLegalProfile ? (
          <p>
            Vor einer Veröffentlichung müssen noch Pflichtangaben ergänzt werden:
            {" "}
            {missingLegalChecklist.join(", ")}.
          </p>
        ) : null
      }
    >
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Angaben zum Anbieter</h2>
        <dl>
          <InfoRow label="Diensteanbieter">{getProviderName()}</InfoRow>
          {hasRepresentative() ? (
            <InfoRow label="Vertreten durch">{legalProfile.representative}</InfoRow>
          ) : null}
          <InfoRow label="Anschrift">
            <div className="space-y-1">
              {getAddressLines().map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </InfoRow>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Kontakt</h2>
        <dl>
          <InfoRow label="E-Mail">
            <a className="underline decoration-stone-300 underline-offset-4" href={`mailto:${legalProfile.email}`}>
              {legalProfile.email}
            </a>
          </InfoRow>
          <InfoRow label="Telefon">
            <a className="underline decoration-stone-300 underline-offset-4" href={`tel:${legalProfile.phone.replace(/\s+/g, "")}`}>
              {legalProfile.phone}
            </a>
          </InfoRow>
        </dl>
      </section>

      {hasCommercialRegister() || hasVatId() ? (
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold tracking-tight">Register und Steuerangaben</h2>
          <dl>
            {hasCommercialRegister() ? (
              <InfoRow label="Registereintrag">
                {legalProfile.registerCourt}, {legalProfile.registerNumber}
              </InfoRow>
            ) : null}
            {hasVatId() ? <InfoRow label="Umsatzsteuer-ID">{legalProfile.vatId}</InfoRow> : null}
          </dl>
        </section>
      ) : null}

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Verbraucherstreitbeilegung</h2>
        <p className="text-base leading-relaxed text-gray-700">{getDisputeResolutionText()}</p>
      </section>
    </LegalPage>
  );
}
